import type { Vector } from "excalibur";
import { Component } from "excalibur";

export type BeeRole = "queen" | "worker";
export type BeeAvailability = "available" | "busy";
export type CellStage =
  | "foundation"
  | "empty"
  | "egg"
  | "larvae"
  | "sealed"
  | "cleaning";
export type CellTypeKind = "none" | "brood" | "pollen" | "nectar";

export type JobKind =
  | "buildCell"
  | "layEgg"
  | "feedLarvae"
  | "feedQueen"
  | "cleanBrood"
  | "foragePollen"
  | "forageNectar"
  | "depositPollen"
  | "depositNectar"
  | "honeyProcess"
  | "guardHive"
  | "adultFeed"
  | "clearCellForRetype";

/** Axial + level identity for a hive cell entity. */
export class CellCoordComponent extends Component {
  constructor(
    public q: number,
    public r: number,
    public level: number,
  ) {
    super();
  }
}

/** Build, brood, and storage state for a cell. */
export class CellStateComponent extends Component {
  built = false;
  stage: CellStage = "foundation";
  buildProgress = 0;
  cellType: CellTypeKind = "none";
  /** Pollen units in this cell when {@link cellType} is `pollen`. */
  pollenStored = 0;
  nectarStored = 0;
  /** Honey units in this cell after nectar is processed; nectar deposits blocked until this is 0. */
  honeyStored = 0;
  honeyProcessingProgress = 0;
  eggTimerMs = 0;
  sealedTimerMs = 0;
  cleaningTimerMs = 0;
  /** Portions of pollen still required before pupation (fed from pollen storage cells). */
  larvaePollenRemaining = 0;
  /** Portions of nectar/honey still required before pupation (fed from nectar cells / honey). */
  larvaeNectarRemaining = 0;
  /** When true, honey job may be interrupted for adult nectar use. */
  honeyProcessingDirty = false;
  /**
   * Desired type after relocation or brood clears; `null` if no change is queued.
   * Does not replace {@link cellType} until the simulation applies it.
   */
  pendingCellType: "brood" | "pollen" | "nectar" | null = null;
}

/** Job queue entity. */
export class JobComponent extends Component {
  reservedBeeIds: number[] = [];
  status: "open" | "active" | "done" = "open";
  pathPoints: Vector[] = [];
  /** Parallel to {@link pathPoints}: hive level after reaching each waypoint (cross-level routes). */
  pathLevels: number[] = [];
  foragePhase: "outbound" | "wait" | "return" | "depositing" | "capacityWait" | "idle" =
    "idle";
  forageWaitMs = 0;
  /** In `capacityWait`, ms until next deposit-capacity check. */
  forageCapacityPollMs = 0;
  carryPayload: "none" | "pollen" | "nectar" | "honey" = "none";
  depositTargetKey: string | null = null;
  /**
   * `adultFeed`: hungry target bee.
   * `feedQueen`: queen entity id to feed.
   */
  adultFeedTargetBeeId: number | null = null;
  /** Self-feed / deposit: hive key of food cell or deposit destination. */
  selfFeedCellKey: string | null = null;
  /** `feedQueen`: royal jelly feed timer while in range. */
  feedQueenTimerMs = 0;
  /** `layEgg`: time queen must remain at brood cell before egg is placed. */
  layEggTimerMs = 0;
  /** `guardHive`: time remaining at entrance. */
  guardHiveTimerMs = 0;
  /** Scratch values for forage targets (world space). */
  scratchX = 0;
  scratchY = 0;
  /** For forage easing: identifies current move leg; bump resets leg length. */
  forageMoveEasingKey = "";
  /** For forage easing: distance to target when {@link forageMoveEasingKey} was set. */
  forageMoveLegInitialDist = 0;
  /**
   * Feed larvae: travel → timed gather at food cell → travel → timed deposit at brood.
   */
  feedLarvaePhase: "toPickup" | "collecting" | "toDeliver" | "depositing" = "toPickup";
  /** Counts down during {@link feedLarvaePhase} `collecting` or `depositing`. */
  feedLarvaePhaseTimerMs = 0;
  feedPickupQ = 0;
  feedPickupR = 0;
  feedPickupLevel = 0;
  /** Active leg: what the bee will pick up at {@link feedPickupQ}/R or is carrying to brood. */
  feedCargoKind: "none" | "pollen" | "nectar" | "honey" = "none";
  /** `clearCellForRetype`: travel to source vs timed relocation ticks. */
  retypePhase: "toCell" | "clearing" = "toCell";
  /** Ms accumulator for {@link retypePhase} `clearing` steps. */
  retypeClearAccumMs = 0;
  constructor(
    public kind: JobKind,
    public priority: number,
    public targetQ: number,
    public targetR: number,
    public targetLevel: number,
    public beesNeeded: number,
  ) {
    super();
  }
}

export class BeeRoleComponent extends Component {
  constructor(public role: BeeRole) {
    super();
  }
}

/** Age in ms for adult workers (emerged at 0; queen has no age component). */
export class BeeAgeComponent extends Component {
  constructor(public ageMs = 0) {
    super();
  }
}

export class BeeWorkComponent extends Component {
  availability: BeeAvailability = "available";
  currentJobEntityId: number | null = null;
  pathIndex = 0;
  /**
   * Snapshot of {@link pathIndex} when {@link pathLegEasingInitialDist} was recorded
   * (path-following easing per waypoint leg).
   */
  pathLegEasingIndexSnapshot = -1;
  /** Distance to current waypoint when the active path leg began (world px). */
  pathLegEasingInitialDist = 0;
  /** World target for idle wandering when unassigned and no eligible open jobs. */
  idleWanderTarget: Vector | null = null;
  /** Time left in the current idle rest (no movement); ms. */
  idleWanderPauseRemainingMs = 0;
}

export class BeeCarryComponent extends Component {
  carry: "none" | "pollen" | "nectar" | "honey" = "none";
}

export class BeeNeedsComponent extends Component {
  hunger = 18;
}

/** Current vertical level for this bee actor. */
export class BeeLevelComponent extends Component {
  constructor(public level: number) {
    super();
  }
  /**
   * When moving between hive levels, the bee holds at the junction waypoint while
   * `verticalTransitionElapsedMs` counts up to the cross-level transition duration; then `level`
   * updates to `verticalTransitionTargetLevel`.
   */
  verticalTransitionTargetLevel: number | null = null;
  verticalTransitionElapsedMs = 0;
}

export class ActiveLevelComponent extends Component {
  activeLevel = 0;
  transition: "idle" | "fadeOut" | "fadeIn" = "idle";
  pendingLevel: number | null = null;
  transitionT = 0;
}

export class QueenTimerComponent extends Component {
  layCooldownMs = 0;
}

/** Global colony clock for HUD day and shared time scale. */
export class ColonyTimeComponent extends Component {
  colonyElapsedMs = 0;
}

/**
 * Per-calendar-year counters and end-of-year review state (60 colony days per year).
 *
 * Counters reset when the player continues after the year-end modal.
 */
/**
 * Run-scoped total honey produced from nectar processing (increment when nectar converts to honey).
 * Used for run summaries. Succession shop spending is based on honey still stored in cells, not this total.
 * Resets when starting a new colony after succession or new game.
 */
export class HoneyRunComponent extends Component {
  honeyProducedThisRun = 0;
}

/** Hive-wide beeswax pool used for building and retyping cells. */
export class BeeswaxComponent extends Component {
  stored = 0;
}

/** Royal jelly pool for succession shop spending (earned from happiness while lineage is active). */
export class RoyalJellyComponent extends Component {
  stored = 0;
  /** Fractional remainder before the next whole royal jelly is minted. */
  accrualBuffer = 0;
  /** Last colony day (1-based) when royal jelly was accrued from happiness. */
  lastAccruedColonyDay = 0;
}

export class YearlyStatsComponent extends Component {
  /** 1-based colony year (increments when the player continues after review). */
  yearNumber = 1;
  /** When true, simulation time and most systems pause until the player continues. */
  isYearReviewOpen = false;
  /** Honey units produced from nectar processing this year. */
  honeyProcessedTotal = 0;
  /** Nectar units deposited from foraging this year. */
  nectarCollectedTotal = 0;
  /** Pollen units deposited from foraging this year. */
  pollenCollectedTotal = 0;
  /** Workers eclosed from brood this year. */
  beesHatchedTotal = 0;
  /**
   * Cumulative seconds all bees with {@link BeeNeedsComponent} spent happy this year.
   */
  happyBeeSecondsTotal = 0;
  /** Bee count snapshot when the year ended (for the review modal). */
  remainingBeesAtYearEnd = 0;
}
